import type { OBB, Vec2 } from '../../geo/types';
import { SHIP_TEMPLATES, type ShipType, type BoatAppearance } from './shipTemplates';
import { rigReach } from './rigging';
import { LEEBOARD_OUTREACH } from './hull';

/**
 * A ship's hull and rig in plan, in its own frame (bow at -X): how far it reaches ahead of its
 * origin, astern of it and to either side; ships are placed so that their plans grown to painter
 * scale never meet. It holds a boat whole and a formed ship's hull and standing rig. A formed
 * ship's course yards reach past its sides (shipYards.ts COURSE_YARD), but never past its plan
 * grown to painter scale, so two ships placed apart never meet, and the painter view draws them
 * within the plan itself (runningRigGeometry). Pennants fly above the rig and gun smoke drifts off
 * the hull ends, so neither is part of the room a neighbour must leave.
 */
export interface ShipPlan {
  readonly fore: number;
  readonly aft: number;
  readonly halfWidth: number;
}

export function shipPlan(type: ShipType, boat?: BoatAppearance): ShipPlan {
  const template = SHIP_TEMPLATES[type];
  const rig = rigReach(template);
  const hullHalfWidth = template.beam / 2 + (template.hasLeeboards ? LEEBOARD_OUTREACH : 0);
  return {
    fore: (template.length / 2 + rig.beyondStem) * (boat?.lengthScale ?? 1),
    aft: (template.length / 2) * (boat?.lengthScale ?? 1),
    halfWidth: Math.max(hullHalfWidth, rig.halfWidth) * (boat?.beamScale ?? 1),
  };
}

/**
 * The plan as a box on the layout around a ship at `position` heading `heading`, grown `scale`
 * times about the ship's origin, as the painter view grows the ship.
 */
export function planBox(plan: ShipPlan, position: Vec2, heading: number, scale = 1): OBB {
  const axisU: Vec2 = [Math.cos(heading), Math.sin(heading)];
  const offset = (scale * (plan.aft - plan.fore)) / 2;
  return {
    center: [position[0] + axisU[0] * offset, position[1] + axisU[1] * offset],
    axisU,
    halfU: (scale * (plan.fore + plan.aft)) / 2,
    halfV: scale * plan.halfWidth,
  };
}
